import Netx from '../bootstrap';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import View from './netx-view';
import _ from 'underscore';
import {logger, spawnNewWindow} from '@netx/core-next';

import type AppView from '@netx/core-app/models/appView';
import type Collection from '@netx/core/lib/netx-collection';
import type Model from '@netx/core/lib/netx-model';

import {FindLegacyAction} from '@netx/core/legacyActionManager';
import {appRouter} from '@netx/core-next/instance/router';
export {isPortal, protectObjectProxy} from '../utils';

export const getViewModel = <TModel extends Model = Model>(
	view: View<TModel | AppView<TModel>>,
): TModel | undefined => {
	const viewData = view.data || {};
	let model = viewData.model || view.model;
	if (model && model.isAppView) {
		model = model.viewModel as TModel;
	}
	return !model ? undefined : (model as TModel);
};

export const getViewCollection = <TCollection extends Collection = Collection>(
	view: View<any, TCollection>,
): TCollection | undefined => {
	const viewData = view.data || {};
	const collection = viewData.collection || view.collection;
	return !collection ? undefined : (collection as TCollection);
};

export const performAction = (actionStr: string, actionArgs: any[] = []) => {
	logger.silly('utils/performAction', actionStr);

	const action = FindLegacyAction(actionStr);
	if (!action) throw new Error(`'${actionStr}' is not a registered action`);

	// Generic handler for all Action clicks:
	if (action.fake) {
		logger.warn(`The action '${action.event}' has not been registered`);
	}
	// or else, if the action itself defined a wrapper, run the arguments through that.
	else if (_.isFunction(action.wrapper)) {
		actionArgs = action.wrapper.apply(this, actionArgs);
		if (!_.isArray(actionArgs)) {
			throw new Error(
				`Action wrapper for '${action.event}' did not return an array`,
			);
		}
	}

	// close any/all open dropdown menus
	dispatcher.trigger('app:close:menus');

	// If this action has a route, route to it
	if (_.isFunction(action.route)) {
		// route can be a function that takes args & returns a string
		appRouter.navigate(action.route.apply(action, actionArgs) as string, true);
	} else if (_.isString(action.route)) {
		// it can also be a plain string.
		// TODO: such actions could be rendered as simple hrefs that can be enabled/disabled, so the browser can do this part ...
		appRouter.navigate(action.route, true);
	}
	// If this action has a link, send the browser to it.
	// (Again, this could have been rendered as a simple href ...)
	else if (_.isString(action.link)) {
		spawnNewWindow(action.link);
		// a link could be of the format "#route", or "/other/site/on/this/host" or even "http://google.com"
		// window.location = action.link;
	}
	// If no view & no route, trigger this action's event
	else {
		actionArgs.unshift(action.event);
		dispatcher.trigger.apply(dispatcher, actionArgs as unknown as any);
	}
};

export const isTouchEnabled = () =>
	'ontouchstart' in window || navigator?.maxTouchPoints > 0;

export default {
	getViewCollection,
	getViewModel,
	performAction,
};
